import React, { useState } from 'react'
import styled from 'styled-components'
import { BackButton } from '../BackButton'
import { Link } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Title } from '../Title'

// A toolbar designed to go at the top of a "Detail" page (e.g. RaceDetail or CandidateDetail)
const DataDetailToolbar = ({
  onBack, // a function or redirect to trigger when the back button is clicked
  navTree = [], // each item should have properties text and optionally "to".
  style,
  title,
  titleSecondary,
  titleComponent, // provide as an alternative to title
  navigationTrailingComponent,
  actionButtonsComponent,
  MoreButton // array of options for the more menu: [{ text: string, onClick: function, disabled?: boolean, icon?: ReactNode }]
}) => {
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const bottomEnabled = Boolean(titleSecondary || titleComponent || title);
  const navTreeTitle = navTree
    .map((item) => typeof item.text === 'string' ? item.text.trim() : '')
    .filter((text) => text && text !== '--')
    .reverse()
    .slice(0, 2)
    .join(' | ');
  const documentTitle = navTreeTitle || title;

  const topContent = () => {
    return (
      <Top>
        {onBack && <BackButton onClick={onBack} />}
        {navTree.length > 0 && (
          <NavTree>
            {navTree.map((item, idx) => (
              <div key={idx}>
                {idx !== 0 && <div>/</div>}
                {item.to ? (
                  <Link to={item.to}>{item.text}</Link>
                ) : (
                  <div>{item.text}</div>
                )}
              </div>
            ))}
          </NavTree>
        )}
        {navigationTrailingComponent && (
          <NavigationTrailing>
            {navigationTrailingComponent}
          </NavigationTrailing>
        )}
      </Top>
    )
  }

  const rightContent = () => {
    const hasActionButtons = Boolean(actionButtonsComponent);
    const hasMoreButton = Array.isArray(MoreButton) && MoreButton.length > 0;
    
    if (!hasActionButtons && !hasMoreButton) {
      return <div></div>;
    }
    
    return (
      <ActionButtonsContainer>
        {actionButtonsComponent}
        {hasMoreButton && (
          <>
            <IconButton
              onClick={(event) => setMoreMenuAnchorEl(event.currentTarget)}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={moreMenuAnchorEl}
              open={Boolean(moreMenuAnchorEl)}
              onClose={() => setMoreMenuAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {MoreButton.map((option, index) => (
                <MenuItem
                  key={index}
                  onClick={() => {
                    if (option.onClick) {
                      option.onClick();
                    }
                    setMoreMenuAnchorEl(null);
                  }}
                  disabled={option.disabled}
                >
                  {option.icon && (
                    <ListItemIcon>
                      {option.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText primary={option.text || option.label} />
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </ActionButtonsContainer>
    );
  }

  const bottomContent = () => (
    <Bottom>
      {titleComponent ? (
        <>{titleComponent}</>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
          }}
        >
          <Typography variant="h1">{title}</Typography>
          {titleSecondary && (
            <Typography
              variant="h3"
              style={{ marginBottom: '4px', marginLeft: '8px' }}
            >
              {titleSecondary}
            </Typography>
          )}
        </div>
      )}
    </Bottom>
  )

  const content = bottomEnabled ? (
      <Wrapper style={style}>
        <Left>
          {topContent()}
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent:'space-between', flex: 1 }}>
            {bottomContent()}
            {rightContent()}
          </div>
        </Left>
      </Wrapper>
  ) : (
      <Wrapper style={style}>
        <Left>
          {topContent()}
        </Left>
        {rightContent()}
      </Wrapper>
  );

  return (
    <>
      <Title>{documentTitle}</Title>
      {content}
    </>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  padding-bottom: 14px;
  border-bottom: solid 1px #cccccc;

  background: rgb(247, 249, 254);
`

const Left = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
`

const Top = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
`

const Bottom = styled.div`
  display: flex;
  margin-top: 28px;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;

  h1 {
    font-size: 28px;
  }
`

const NavTree = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: 10px;

  div {
    display: flex;
    flex-direction: row;
  }

  div > div,
  div > a {
    margin-left: 10px;
    ${(props) => props.theme.font.normal}
    font-size: 16px;
  }

  div > a {
    text-decoration: underline;
    color: ${(props) => props.theme.colors.indigo};
  }
`

const NavigationTrailing = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  padding-left: 24px;
`

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`

export { DataDetailToolbar }
